import { useEffect, useState } from 'react'
import type { Stats } from '@/lib/api'

/**
 * The "Millions of colors still available" donut.
 *
 * The ring uses illustrative proportions (at true scale the 138 reserved colors would be invisible), but the numbers
 * are real: they come from /api/stats, which the CDN caches for an hour.
 * Hover or focus a slice or a legend entry to read its count and true share
 * in the center; the ring draws itself on first view.
 */
const SLICES = [
  { key: 'reserved', label: 'Reserved', share: 1, color: 'var(--color-chart-reserved)' },
  { key: 'premium', label: 'Premium', share: 1.5, color: 'var(--color-chart-premium)' },
  { key: 'registered', label: 'Registered', share: 6.5, color: 'var(--color-chart-registered)' },
  { key: 'available', label: 'Available', share: 91, color: 'var(--color-chart-available)' },
] as const

type SliceKey = (typeof SLICES)[number]['key']

const FALLBACK: Stats = { total: 16_777_216, reserved: 140, registered: 1_313_303, premium: 12_300, available: 15_451_473 }

function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 1 : 2)}M`
  if (n >= 10_000) return `${Math.round(n / 1000)}K`
  return n.toLocaleString('en-US')
}

function percent(n: number, total: number): string {
  const p = (n / total) * 100
  if (p >= 10) return `${p.toFixed(0)}%`
  if (p >= 1) return `${p.toFixed(1)}%`
  if (p >= 0.01) return `${p.toFixed(2)}%`
  return '<0.01%'
}

export default function AvailabilityDonut({ stats, className = '' }: { stats?: Stats | null; className?: string }) {
  const s = stats ?? FALLBACK
  const [active, setActive] = useState<SliceKey | null>(null)
  const [drawn, setDrawn] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setDrawn(true), 50)
    return () => clearTimeout(t)
  }, [])

  // Outer edge of a hovered slice is r + (stroke + 4) / 2 = 49, inside the 100-unit viewBox.
  const r = 38
  const stroke = 18
  const c = 2 * Math.PI * r
  const totalShare = SLICES.reduce((sum, x) => sum + x.share, 0)
  const arcs = SLICES.map((x, i) => ({
    ...x,
    len: (x.share / totalShare) * c,
    offset: SLICES.slice(0, i).reduce((sum, p) => sum + (p.share / totalShare) * c, 0),
    count: s[x.key],
  }))

  const current = active ? arcs.find((a) => a.key === active) : null
  const centerValue = current ? compact(current.count) : compact(s.available)
  const centerLabel = current ? `${current.label} · ${percent(current.count, s.total)}` : 'available'

  return (
    <figure className={`flex flex-col items-center ${className}`}>
      <svg
        viewBox="0 0 100 100"
        className="w-full max-w-[360px] select-none overflow-visible"
        role="img"
        aria-label={`Of ${s.total.toLocaleString('en-US')} colors, ${s.available.toLocaleString('en-US')} are still available.`}
        onMouseLeave={() => setActive(null)}
      >
        <g transform="rotate(-90 50 50)">
          {arcs.map((a) => {
            const dim = active !== null && active !== a.key
            const grow = active === a.key
            return (
              <circle
                key={a.key}
                cx="50"
                cy="50"
                r={r}
                fill="none"
                stroke={a.color}
                strokeWidth={grow ? stroke + 4 : stroke}
                strokeDasharray={drawn ? `${a.len} ${c - a.len}` : `0 ${c}`}
                strokeDashoffset={-a.offset}
                opacity={dim ? 0.35 : 1}
                tabIndex={0}
                aria-label={`${a.label}: ${a.count.toLocaleString('en-US')} colors`}
                onMouseEnter={() => setActive(a.key)}
                onFocus={() => setActive(a.key)}
                onBlur={() => setActive(null)}
                className="cursor-pointer outline-none transition-[stroke-dasharray,stroke-width,opacity] duration-700 ease-out motion-reduce:transition-none"
                style={{ transitionDelay: drawn ? '0ms' : `${a.offset * 3}ms` }}
              />
            )
          })}
        </g>
        <text x="50" y="49" textAnchor="middle" className="fill-black font-display" style={{ fontSize: 12, fontWeight: 500 }} aria-hidden="true">
          {centerValue}
        </text>
        <text x="50" y="58" textAnchor="middle" className="fill-neutral-500 font-body" style={{ fontSize: 4.6 }} aria-hidden="true">
          {centerLabel}
        </text>
      </svg>
      <figcaption className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2 font-body text-base">
        {arcs.map((a) => (
          <button
            key={a.key}
            type="button"
            onMouseEnter={() => setActive(a.key)}
            onMouseLeave={() => setActive(null)}
            onFocus={() => setActive(a.key)}
            onBlur={() => setActive(null)}
            className={`inline-flex items-center gap-2 rounded px-1 transition-opacity ${active !== null && active !== a.key ? 'opacity-50' : ''}`}
            aria-label={`${a.label}: ${a.count.toLocaleString('en-US')} colors, ${percent(a.count, s.total)}`}
          >
            <span className="inline-block h-4 w-4" style={{ backgroundColor: a.color }} aria-hidden="true" />
            {a.label}
            <span className="text-sm text-neutral-500">{compact(a.count)}</span>
          </button>
        ))}
      </figcaption>
    </figure>
  )
}
